import api from "@/lib/api";
import { useAuthStore } from "@/stores/use-auth-store";
import { usePendingOffersStore } from "@/stores/use-pending-offers-store";
import { syncBookingFromWebSocket } from "@/hooks/queries/use-bookings-query";
import { queryClient } from "@/lib/query-client";

export interface BookingLiveState {
  booking_id: number;
  status: string;
  quote_count: number;
  latest_quote?: {
    id: number;
    amount: string;
    driver_id: number;
  } | null;
  assigned_driver_id?: number | null;
  accepted_quote_id?: number | null;
}

class NotificationFallbackSyncService {
  private driverInterval: ReturnType<typeof setInterval> | null = null;
  private bookingInterval: ReturnType<typeof setInterval> | null = null;
  private customerInterval: ReturnType<typeof setInterval> | null = null;
  private activeBookingId: number | null = null;

  async syncDriverOffers(): Promise<void> {
    try {
      const response = await api.get("/drivers/pending-offers");
      const offers = response.data.data ?? [];

      offers.forEach((offer: any) => {
        usePendingOffersStore.getState().addOffer({
          offer_id: offer.id,
          booking_id: offer.booking_id,
          vehicle_id: offer.vehicle_id,
          from_address: offer.booking?.from_area ?? offer.booking?.from_address,
          to_address: offer.booking?.to_area ?? offer.booking?.to_address,
          service_type: offer.booking?.service_type_label,
          scheduled_at: offer.booking?.scheduled_at,
          expires_at: offer.expires_at,
          priority: false,
          job_category: offer.booking?.job_category,
          job_category_label: offer.booking?.job_category_label,
        });
      });
    } catch (error) {
      console.warn("Driver pending offers sync failed", error);
    }
  }

  async syncBookingLiveState(
    bookingId: number,
  ): Promise<BookingLiveState | null> {
    try {
      const response = await api.get(`/bookings/${bookingId}/live-state`);
      return response.data.data as BookingLiveState;
    } catch (error) {
      console.warn("Booking live state sync failed", error);
      return null;
    }
  }

  startDriverPolling(): void {
    this.stopDriverPolling();
    this.syncDriverOffers();
    this.driverInterval = setInterval(() => {
      this.syncDriverOffers();
    }, 15000);
  }

  stopDriverPolling(): void {
    if (this.driverInterval) {
      clearInterval(this.driverInterval);
      this.driverInterval = null;
    }
  }

  startCustomerPolling(
    bookingId: number,
    onUpdate: (state: BookingLiveState) => void,
  ): void {
    this.stopCustomerPolling();
    this.activeBookingId = bookingId;

    const poll = async () => {
      const state = await this.syncBookingLiveState(bookingId);
      if (state) {
        onUpdate(state);
      }
    };

    poll();
    this.customerInterval = setInterval(poll, 15000);
  }

  stopCustomerPolling(): void {
    if (this.customerInterval) {
      clearInterval(this.customerInterval);
      this.customerInterval = null;
    }
    this.activeBookingId = null;
  }

  async startBookingPolling(bookingId: number): Promise<void> {
    this.stopBookingPolling();
    this.activeBookingId = bookingId;
    const poll = async () => {
      try {
        const response = await api.get(
          `/bookings/${this.activeBookingId}/track`,
        );
        syncBookingFromWebSocket(queryClient, response.data.data.booking);
      } catch (error) {
        console.warn("Booking tracking sync failed", error);
      }
    };
    if (this.activeBookingId) {
      poll();
      this.bookingInterval = setInterval(poll, 15000);
    }
  }

  stopBookingPolling(): void {
    if (this.bookingInterval) {
      clearInterval(this.bookingInterval);
      this.bookingInterval = null;
    }
  }

  async sync(): Promise<void> {
    const isDriver = useAuthStore.getState().user?.role === "driver";
    if (isDriver) {
      await this.syncDriverOffers();
    }

    if (this.activeBookingId) {
      await this.syncBookingLiveState(this.activeBookingId);
    }
  }
}

export const notificationFallbackSync = new NotificationFallbackSyncService();
